import { cn } from "@productlane/lib/src/browser"

import type { ReportingTone } from "./theme"
import { ReportingChartFrame } from "./chart-frame"
import { categoricalColor, toneColor } from "./theme"

export interface ReportingPipelineSegment {
	label: string
	value: number
	tone?: ReportingTone
}

interface ReportingPipelineCardProps {
	title: string
	description?: string
	segments: ReportingPipelineSegment[]
	isLoading?: boolean
	className?: string
}

export const ReportingPipelineCard = ({
	title,
	description,
	segments,
	isLoading,
	className,
}: ReportingPipelineCardProps) => {
	const total = segments.reduce((sum, s) => sum + s.value, 0)
	const segmentColor = (segment: ReportingPipelineSegment, index: number) =>
		segment.tone ? toneColor(segment.tone) : categoricalColor(index)

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={total === 0}
			emptyMessage="No conversations in the pipeline"
			className={className}
		>
			<div className="space-y-4">
				<div className="flex h-3 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
					{segments.map((segment, index) => {
						if (segment.value <= 0) return null
						const widthPct = Math.max(2, (segment.value / total) * 100)
						return (
							<div
								key={segment.label}
								className="h-full first:rounded-l-full last:rounded-r-full"
								style={{
									width: `${widthPct}%`,
									backgroundColor: segmentColor(segment, index),
								}}
								title={`${segment.label}: ${segment.value}`}
							/>
						)
					})}
				</div>
				<ul className="grid grid-cols-3 gap-3">
					{segments.map((segment, index) => {
						const pct =
							total > 0 ? Math.round((segment.value / total) * 1000) / 10 : 0
						return (
							<li key={segment.label} className="min-w-0">
								<div className="mb-1 flex items-center gap-1.5">
									<span
										className="h-2 w-2 shrink-0 rounded-full"
										style={{
											backgroundColor: segmentColor(segment, index),
										}}
									/>
									<span className="truncate text-xs text-tertiary">
										{segment.label}
									</span>
								</div>
								<p className="text-xl font-semibold text-primary">
									{segment.value}
								</p>
								<p className={cn("text-[11px] text-tertiary")}>{pct}%</p>
							</li>
						)
					})}
				</ul>
			</div>
		</ReportingChartFrame>
	)
}
